/**
 * Notification Manager Service
 * Manages toast notifications across the application
 */

class NotificationManager {
  constructor() {
    this.listeners = [];
    this.notifications = [];
    this.nextId = 1;
  }

  /**
   * Subscribe to notification updates
   */
  subscribe(callback) {
    this.listeners?.push(callback);
    return () => {
      this.listeners = this.listeners?.filter(listener => listener !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notify() {
    this.listeners?.forEach(listener => listener(this.notifications));
  }

  /**
   * Show notification
   */
  show(message, type = 'info', options = {}) {
    const notification = {
      id: this.nextId++,
      message,
      type,
      duration: options?.duration ?? 5000,
      action: options?.action,
      timestamp: Date.now()
    };

    this.notifications?.push(notification);
    this.notify();

    return notification?.id;
  }

  /**
   * Remove notification
   */
  remove(id) {
    this.notifications = this.notifications?.filter(n => n?.id !== id);
    this.notify();
  }

  /**
   * Show success notification
   */
  success(message, options = {}) {
    return this.show(message, 'success', options);
  }

  /**
   * Show error notification
   */
  error(message, options = {}) {
    return this.show(message, 'error', { duration: 7000, ...options });
  }

  /**
   * Show warning notification
   */
  warning(message, options = {}) {
    return this.show(message, 'warning', options);
  }

  /**
   * Show info notification
   */
  info(message, options = {}) {
    return this.show(message, 'info', options);
  }

  /**
   * Show retry notification with action
   */
  retryNotification(message, onRetry) {
    return this.error(message, {
      duration: 10000,
      action: {
        label: 'Retry Now',
        onClick: onRetry
      }
    });
  }

  /**
   * Clear all notifications
   */
  clearAll() {
    this.notifications = [];
    this.notify();
  }
}

const notificationManager = new NotificationManager();
export default notificationManager;